/**
 * Product catalogue of the B2B automation ecosystem.
 * Source of truth is the public offer document (`public/docs/B2B_Tech_Insight_Automatizacni_ekosystem_nabidka.pdf`);
 * page numbers below point into that PDF. Used by the Products section and the inquiry wizard.
 */

export const OFFER_PDF_URL = "/docs/B2B_Tech_Insight_Automatizacni_ekosystem_nabidka.pdf"
export const BASICS_PDF_URL = "/docs/b2b_integrace_zaklady.pdf"
export const INQUIRY_EMAIL = "info@b2btechinsight.cz"

export type ProductId =
	| "gorbus"
	| "neuron-machine"
	| "light-gateway"
	| "brokerhflow"
	| "custom-app"
	| "make-sdk"

export interface ProductMetric {
	value: string
	label: string
}

export interface Product {
	id: ProductId
	name: string
	/** One-line role in the ecosystem */
	role: string
	/** Technology stack shown as a tag */
	tech: string
	/** Short marketing description */
	summary: string
	/** Key capabilities (bullet list) */
	highlights: string[]
	/** Reference use case from the offer document */
	useCase: {
		title: string
		department: string
		text: string
		metrics: ProductMetric[]
	}
	/** Page in the offer PDF with the product description */
	pdfPage: number
	/** Page in the offer PDF with the use case */
	useCasePdfPage?: number
	/** Optional badge, e.g. for partner products */
	badge?: string
	/** Lucide icon name is resolved in the component; keep it as a plain key here */
	icon: "bus" | "cpu" | "gateway" | "layout" | "app" | "plug"
}

/**
 * Order matters: it is the display order on the landing page. Make SDK is intentionally
 * last — it is a partner product (co-operation with the Make platform), the others are ours.
 */
export const PRODUCTS: Product[] = [
	{
		id: "gorbus",
		name: "GoRBus",
		role: "Centrální sběrnice a datový dispečer",
		tech: "Go (Golang)",
		icon: "bus",
		summary:
			"Páteř celého ekosystému. Deterministicky směruje události z HTTP, souborových složek a databází do správných modulů – bez AI, bez překvapení a bez úzkého hrdla.",
		highlights: [
			"Více než 10 000 událostí za sekundu, latence pod 10 ms",
			"Providers → Events → Routines: filtr, mapování polí, převod formátu",
			"Izolované rutiny: dávky nekritických souborů nezpomalí prioritní API"
		],
		useCase: {
			title: "Rychlé finanční transakce bez AI",
			department: "Finance / e-commerce / logistika",
			text: "Platební požadavek z terminálu nebo e-shopu projde deterministickou rutinou: zápis do databáze, zpracování platby, odpověď klientovi. AI je zde záměrně vyloučena.",
			metrics: [
				{ value: "< 10 ms", label: "garantovaná latence" },
				{ value: "99,999 %", label: "dostupnost" },
				{ value: "> 10 000 / s", label: "propustnost událostí" }
			]
		},
		pdfPage: 8,
		useCasePdfPage: 17
	},
	{
		id: "neuron-machine",
		name: "Neuron Machine",
		role: "Robustní a bezpečné ETL jádro",
		tech: "Multi-proces Master/Child",
		icon: "cpu",
		summary:
			"Událostmi řízený orchestrátor pro těžké asynchronní úlohy: parsování velkých či poškozených PDF, OCR a extrakce dat lokální i cloudovou AI – v izolovaných podprocesech, které nikdy neshodí server.",
		highlights: [
			"Automatická extrakce kusovníků (BOM) z PDF výkresů, účetních závěrek a faktur",
			"Lokální modely (LLaMA přes Ollama) pro citlivá data, Groq / Gemini / Claude pro rychlé dávky",
			"Výstup rovnou jako Excel, Word nebo JSON pro import do ERP"
		],
		useCase: {
			title: "Hromadné zpracování technické dokumentace (BOM)",
			department: "Nákup / konstrukce",
			text: "Stovky PDF výkresů se zpracují na pozadí. Nákupní oddělení dostane hotový Excel se soupisem součástek připravený k importu do ERP.",
			metrics: [
				{ value: "20 min → 5 s", label: "doba zpracování výkresu" },
				{ value: "−40 %", label: "nákladů na administrativu" },
				{ value: "100 %", label: "pokrytí dávky bez ručního zásahu" }
			]
		},
		pdfPage: 5,
		useCasePdfPage: 16
	},
	{
		id: "light-gateway",
		name: "Light B2B Gateway",
		role: "Komunikační brána a AI rozhraní",
		tech: "Node.js 20+, Express 5",
		icon: "gateway",
		summary:
			"Jednotný vstupní bod pro vaše systémy i AI: přijímá JSON přes API, nahrané dokumenty nebo soubory ze sledovaných složek a provede je řetězcem pluginů definovaným v JSON konfiguraci.",
		highlights: [
			"Směrování úloh na Azure OpenAI, agenty v Dify, Claude či lokální LLaMA",
			"Výstup jako JSON do webu, nebo hotový Word / Excel dokument",
			"Trigger na pozadí: nový soubor ve složce spustí zpracování sám"
		],
		useCase: {
			title: "Inteligentní lektor pro e-learning",
			department: "HR / vzdělávání",
			text: "Dotazy ze vzdělávacího portálu směřuje brána na AI agenta v kontextu školení. Student dostane okamžitou, kontextově přesnou odpověď.",
			metrics: [
				{ value: "−60 %", label: "zátěže lektorů a helpdesku" },
				{ value: "24/7", label: "dostupnost individuální podpory" },
				{ value: "0 s", label: "čekání na odpověď" }
			]
		},
		pdfPage: 7,
		useCasePdfPage: 18
	},
	{
		id: "brokerhflow",
		name: "BrokerHFlow",
		role: "Vizuální řídicí centrum a klientský portál",
		tech: "React + Rust",
		icon: "layout",
		summary:
			"Webový portál, ve kterém zaměstnanci nahrají dokument, vidí originální PDF bok po boku s daty vytěženými AI, opraví je a schválí. Člověk má poslední slovo (Human-in-the-Loop).",
		highlights: [
			"Side-by-side kontrola dokumentů: PDF vlevo, strukturovaná data vpravo",
			"Monitoring datových toků a stavu služeb v reálném čase",
			"Export schválených dat do Excelu / Wordu nebo přímo do ERP"
		],
		useCase: {
			title: "Zpracování QA dokumentů s lidskou kontrolou",
			department: "Kvalita / právní / účetní",
			text: "Dokumentace kvality a audity projdou AI extrakcí, pracovník výsledek zkontroluje v interaktivním náhledu a teprve schválená data putují do ERP.",
			metrics: [
				{ value: "−80 %", label: "času na revizi a přepis" },
				{ value: "100 %", label: "garance správnosti dat v ERP" },
				{ value: "Zero Trust AI", label: "odpovědnost vždy u operátora" }
			]
		},
		pdfPage: 9,
		useCasePdfPage: 19
	},
	{
		id: "custom-app",
		name: "Aplikace na zakázku",
		role: "Weby, portály a intranetová rozhraní na míru",
		tech: "React, Node.js, Rust",
		icon: "app",
		summary:
			"Výkonný backend je jen polovina úspěchu. Druhou je rozhraní, které zapadne do každodenní rutiny vašich lidí: nahrát soubor, zkontrolovat náhled, spustit analýzu jedním kliknutím.",
		highlights: [
			"Webová i intranetová řešení navržená podle vašich procesů",
			"Napojení na celý ekosystém (GoRBus, Neuron Machine, brána) přes API",
			"Včetně multimodální AI, např. analýza výkresů modelem Gemini"
		],
		useCase: {
			title: "Interní AI portál pro technickou dokumentaci",
			department: "Konstrukce / nákup",
			text: "Uživatel nahraje PDF výkres, spustí analýzu multimodálním modelem a v prohlížeči vidí náhled výkresu i vytěžená data – bez znalosti infrastruktury pod povrchem.",
			metrics: [
				{ value: "1 klik", label: "spuštění komplexní analýzy" },
				{ value: "0 školení", label: "rozhraní podle vašich procesů" },
				{ value: "API", label: "napojení na stávající systémy" }
			]
		},
		pdfPage: 12
	},
	{
		id: "make-sdk",
		name: "Make SDK",
		role: "Agilní integrační adaptér pro platformu Make.com",
		tech: "Node.js, JSON pipeline",
		icon: "plug",
		badge: "Spolupráce s Make",
		summary:
			"Doplněk pro firmy, které už používají Make.com. Běží lokálně, bezpečně vytěží on-premise databáze a soubory a pošle je na webhook do Make, kde navazuje cloudová byznysová logika a lidské schvalování.",
		highlights: [
			"Pipeline EXTRACT → TRANSFORM → LOAD definovaná v JSON",
			"Připojení k PostgreSQL, MS SQL, MongoDB i souborovému systému bez otevřených portů",
			"Blok ON_ERROR s automatickým rollbackem – lokální prostředí zůstane konzistentní"
		],
		useCase: {
			title: "Automatizace nekritických reportů",
			department: "Controlling / management",
			text: "SDK vytáhne nekritická data z lokální databáze a pošle je do Make.com. Výsledkem jsou statistiky v Google Sheets a manažerský souhrn e-mailem – bez ručního dotazování databáze.",
			metrics: [
				{ value: "15 h", label: "úspora měsíčně v controllingu" },
				{ value: "100 %", label: "eliminace chyb přepisu" },
				{ value: "0", label: "otevřených portů do interní sítě" }
			]
		},
		pdfPage: 6,
		useCasePdfPage: 15
	}
]

export const PRODUCT_BY_ID: Record<ProductId, Product> = PRODUCTS.reduce(
	(acc, product) => ({ ...acc, [product.id]: product }),
	{} as Record<ProductId, Product>
)

/** Link into the offer PDF opened on a given page (supported by browser PDF viewers). */
export const offerPdfPage = (page?: number) => (page ? `${OFFER_PDF_URL}#page=${page}` : OFFER_PDF_URL)
